// Every word anyone at a site sees: staff phones (the alert titles, the lock screen and
// red card, the app's screens) and the ops tablet, including what a change does, shown on
// the tablet before someone approves it there. A
// site can offer any language: its words travel in the site's signed bundle (`phrases`),
// next to the alert instructions (`messages`), so a new language needs no app update.
// English and Arabic are built in as the starting point. A language is only offered when
// every word is there (see completeLanguages), so nobody ever sees a mix.
//
// Words can hold blanks the app fills in, like {site} or {message}. A translation has to
// keep exactly the blanks its English has.
//
// Adding a word: add its key below, with English and Arabic, then run `npm run vectors`
// here and `python3 tools/make_phrases_swift.py` (the apps' copy). Every other language
// is then short of that word, so it isn't offered until the console fills it in.
// The Arabic is placeholder wording until a native speaker has checked it.

import type { Level, SiteBundle } from './formats.ts';

export const PHRASE_KEYS = [
  // Alert titles
  'title.alert',
  'title.shelter',
  'title.evacuate',
  'title.clear',
  'title.false_alarm',
  // Lock screen and red card
  'zone.one',
  'zone.many',
  'lock.in.one',
  'lock.in.many',
  'lock.entered.one',
  'lock.entered.many',
  'lock.elsewhere.one',
  'lock.elsewhere.many',
  'lock.unknown.one',
  'lock.unknown.many',
  'lock.stayAway',
  'lock.takeCare',
  'test.title',
  'test.body',
  'test.name',
  'verified',
  'unverified.title',
  'unverified.body',
  'unverified.short',
  // The app's screens
  'perm.title',
  'perm.text',
  'perm.loud',
  'perm.loudText',
  'perm.location',
  'perm.locationText',
  'perm.allow',
  'home.noAlerts',
  'home.yourZone',
  'home.zoneUnknown',
  'home.notInZone',
  'home.turnOn',
  'home.language',
  'home.languageText',
  'home.change',
  'home.recent',
  'home.lastHeard',
  'alert.notConfirmed',
  'alert.close',
  'alert.turnOnLocation',
  'alert.locationOff',
  'alert.locationUnknown',
  'alert.inside',
  'alert.notIn',
  'alert.near',
  'off.title',
  'off.text',
  'off.zone',
  // The ops tablet
  'ops.centre',
  'ops.offline',
  'ops.alertIn.one',
  'ops.alertIn.many',
  'ops.legendDanger',
  'ops.zoneStatus',
  'ops.tapHint',
  'ops.zoneClear',
  'ops.sent',
  'ops.sending',
  'ops.noPhones',
  'ops.delivered.one',
  'ops.delivered.many',
  'ops.retrying',
  'ops.sendTo.one',
  'ops.sendTo.many',
  'ops.cancel',
  'ops.level',
  'ops.holdHint',
  'ops.hold',
  'ops.holdSending',
  'ops.notSent',
  'ops.notSentOffline',
  'ops.notSentOff',
  'ops.updateFailed',
  'ops.languageText',
  'ops.testText',
  'ops.off',
  'ops.approve.setUpTitle',
  'ops.approve.changeTitle',
  'ops.approve.text',
  'ops.approve.prepared',
  'ops.approve.hold',
  'ops.approve.holdHint',
  'ops.approve.approving',
  'ops.approve.failed',
  'ops.approve.waiting',
  'ops.approve.review',
  'ops.approve.later',
  // What a change does: shown on the tablet before it's approved
  'change.siteSetUp.one',
  'change.siteSetUp.many',
  'change.renameSite',
  'change.addZone',
  'change.renameZone',
  'change.redrawZone',
  'change.removeZone',
  'change.mapArea',
  'change.mapPicture',
  'change.addTablet',
  'change.removeTablet',
  'change.addLanguage',
  'change.changeWording',
  'change.removeLanguage',
  'change.more',
  'change.noChange',
  'change.separator',
] as const;

export type PhraseKey = (typeof PHRASE_KEYS)[number];

/** The words the apps carry: every word (staff phone and ops tablet). */
export const APP_PHRASE_KEYS: readonly PhraseKey[] = PHRASE_KEYS;
export type Phrases = Record<PhraseKey, string>;

/** How the console groups the words, in the order staff meet them. */
export const PHRASE_GROUPS: { name: string; keys: PhraseKey[] }[] = [
  { name: 'Alert titles', keys: PHRASE_KEYS.filter((k) => k.startsWith('title.')) },
  {
    name: 'Lock screen and red card',
    keys: PHRASE_KEYS.filter((k) => /^(zone|lock|test|verified|unverified)\b/.test(k)),
  },
  { name: 'Staff phone screens', keys: PHRASE_KEYS.filter((k) => /^(perm|home|alert|off)\./.test(k)) },
  { name: 'Ops tablet', keys: PHRASE_KEYS.filter((k) => k.startsWith('ops.')) },
  { name: 'Ops tablet: what a change does', keys: PHRASE_KEYS.filter((k) => k.startsWith('change.')) },
];

export const STANDARD_PHRASES: { en: Phrases; ar: Phrases } = {
  en: {
    'title.alert': 'Be alert',
    'title.shelter': 'Take shelter now',
    'title.evacuate': 'Evacuate',
    'title.clear': 'All clear',
    'title.false_alarm': 'False alarm',
    'zone.one': 'Zone {zone}',
    'zone.many': 'Zones {zones}',
    'lock.in.one': "You're in this zone. {message}",
    'lock.in.many': "You're in one of these zones. {message}",
    'lock.entered.one': "You've just entered this zone. {message}",
    'lock.entered.many': "You've just entered one of these zones. {message}",
    'lock.elsewhere.one': "You're not in this zone. {then}",
    'lock.elsewhere.many': "You're not in these zones. {then}",
    'lock.unknown.one': "Your location is unknown. If you're in this zone: {message}",
    'lock.unknown.many': "Your location is unknown. If you're in one of these zones: {message}",
    'lock.stayAway': "Don't travel there.",
    'lock.takeCare': "Take care if you're nearby.",
    'test.title': 'TEST ALERT',
    'test.body': 'This is a Z.A.S. test. No action needed.',
    'test.name': 'Monthly test',
    verified: 'Verified · {site}',
    'unverified.title': 'UNVERIFIED ALERT',
    'unverified.body': "Z.A.S. couldn't confirm this came from your site's operations centre. Check with your supervisor before acting.",
    'unverified.short': 'Unverified alert',
    'perm.title': 'Two things to allow',
    'perm.text': '{site} uses these to warn you in time.',
    'perm.loud': 'Loud alerts',
    'perm.loudText': 'So an alert for your zone reaches you straight away, even on silent.',
    'perm.location': 'Location, all the time',
    'perm.locationText': "Your phone works out which zone you're in. Your location never leaves this phone.",
    'perm.allow': 'Allow',
    'home.noAlerts': 'No alerts at {site}',
    'home.yourZone': 'Your zone',
    'home.zoneUnknown': 'Location off or unknown. Every alert will be loud.',
    'home.notInZone': 'Not in any zone',
    'home.turnOn': 'Turn on',
    'home.language': 'Language',
    'home.languageText': 'Alerts and your lock screen use this language from the next alert. These are the languages {site} offers.',
    'home.change': 'Change',
    'home.recent': 'Recent alerts',
    'home.lastHeard': 'Last heard from Z.A.S.',
    'alert.notConfirmed': 'Not confirmed as coming from your site',
    'alert.close': 'Close',
    'alert.turnOnLocation': 'Turn on location',
    'alert.locationOff': "Location is off, so we can't tell where you are. If you're in {zones}: {message}",
    'alert.locationUnknown': "Your location is unknown right now. If you're in {zones}: {message}",
    'alert.inside': "You're inside zone {zone}. {message}",
    'alert.notIn': "You're not in {zones}. Don't travel there.",
    'alert.near': "Be alert if you're near {zones}.",
    'off.title': 'Z.A.S. is switched off at this site',
    'off.text': "Alerts will not be sent to this phone. Don't rely on it for warnings. Ask your supervisor.",
    'off.zone': 'Not tracked while Z.A.S. is off',
    'ops.centre': '{site} · Operations centre',
    'ops.offline': "Can't reach Z.A.S.",
    'ops.alertIn.one': 'Alert in 1 zone',
    'ops.alertIn.many': 'Alert in {count} zones',
    'ops.legendDanger': 'Shelter or evacuate',
    'ops.zoneStatus': 'Zone status',
    'ops.tapHint': 'Tap zones on the map to send an alert',
    'ops.zoneClear': 'Clear',
    'ops.sent': 'Sent · {title}',
    'ops.sending': 'Sending to phones…',
    'ops.noPhones': 'No phones have joined this site yet',
    'ops.delivered.one': 'Delivered to {accepted} of 1 phone',
    'ops.delivered.many': 'Delivered to {accepted} of {attempted} phones',
    'ops.retrying': '{failed} retrying',
    'ops.sendTo.one': 'Send to 1 zone',
    'ops.sendTo.many': 'Send to {count} zones',
    'ops.cancel': 'Cancel',
    'ops.level': 'Level',
    'ops.holdHint': 'Hold for 1 second. Sends straight away.',
    'ops.hold': 'Hold to send',
    'ops.holdSending': 'Sending…',
    'ops.notSent': 'Not sent. Try again, and call the Z.A.S. team if it keeps happening.',
    'ops.notSentOffline': "Not sent: can't reach Z.A.S. Check the connection and try again.",
    'ops.notSentOff': 'Not sent: Z.A.S. is switched off for this site. Call the Z.A.S. team.',
    'ops.updateFailed': "A site update didn't pass the security checks. Keeping the current set-up.",
    'ops.languageText': 'The tablet shows everything in this language. These are the languages {site} offers.',
    'ops.testText': 'Every phone that has joined {site} gets a test alert. No zone changes. Staff see:',
    'ops.off': "Z.A.S. is switched off for this site. Alerts can't be sent. Call the Z.A.S. team.",
    'ops.approve.setUpTitle': 'Approve the site set-up',
    'ops.approve.changeTitle': 'Approve a change to {site}',
    'ops.approve.text': 'Check the map and the list. Once you approve, it goes to this tablet and to every staff phone.',
    'ops.approve.prepared': 'Prepared by {name} · {time}',
    'ops.approve.hold': 'Hold to approve',
    'ops.approve.holdHint': 'Hold for 1 second to approve.',
    'ops.approve.approving': 'Approving…',
    'ops.approve.failed': "The approval didn't go through. Try again.",
    'ops.approve.waiting': 'A change is waiting for your approval',
    'ops.approve.review': 'Review',
    'ops.approve.later': 'Later',
    'change.siteSetUp.one': 'Site set up: 1 zone, {languages}',
    'change.siteSetUp.many': 'Site set up: {count} zones, {languages}',
    'change.renameSite': 'Rename site to {name}',
    'change.addZone': 'Add zone {zone} · {name}',
    'change.renameZone': 'Rename zone {zone} to {name}',
    'change.redrawZone': 'Redraw zone {zone} · {name}',
    'change.removeZone': 'Remove zone {zone} · {name}',
    'change.mapArea': 'Change the map area',
    'change.mapPicture': 'Change the map picture',
    'change.addTablet': 'Add ops tablet: {name}',
    'change.removeTablet': 'Remove ops tablet: {name}',
    'change.addLanguage': 'Add language: {language}',
    'change.changeWording': 'Change wording: {language}',
    'change.removeLanguage': 'Remove language: {language}',
    'change.more': 'and {count} more',
    'change.noChange': 'No visible change (re-signed)',
    'change.separator': '; ',
  },
  ar: {
    'title.alert': 'كن متيقظاً',
    'title.shelter': 'احتمِ الآن',
    'title.evacuate': 'أخلِ الموقع',
    'title.clear': 'زال الخطر',
    'title.false_alarm': 'إنذار خاطئ',
    'zone.one': 'المنطقة {zone}',
    'zone.many': 'المناطق {zones}',
    'lock.in.one': 'أنت في هذه المنطقة. {message}',
    'lock.in.many': 'أنت في إحدى هذه المناطق. {message}',
    'lock.entered.one': 'دخلت هذه المنطقة للتو. {message}',
    'lock.entered.many': 'دخلت إحدى هذه المناطق للتو. {message}',
    'lock.elsewhere.one': 'لست في هذه المنطقة. {then}',
    'lock.elsewhere.many': 'لست في هذه المناطق. {then}',
    'lock.unknown.one': 'موقعك غير معروف. إذا كنت في هذه المنطقة: {message}',
    'lock.unknown.many': 'موقعك غير معروف. إذا كنت في إحدى هذه المناطق: {message}',
    'lock.stayAway': 'لا تتوجه إلى هناك.',
    'lock.takeCare': 'احذر إذا كنت قريباً.',
    'test.title': 'تنبيه تجريبي',
    'test.body': 'هذا اختبار لنظام Z.A.S. لا حاجة لأي إجراء.',
    'test.name': 'الاختبار الشهري',
    verified: 'موثّق · {site}',
    'unverified.title': 'تنبيه غير موثّق',
    'unverified.body': 'لم نتمكن من التأكد أن هذا صادر عن مركز العمليات في موقعك. تحقق من مشرفك قبل التصرف.',
    'unverified.short': 'تنبيه غير موثّق',
    'perm.title': 'أمران يجب السماح بهما',
    'perm.text': 'يستخدم {site} هذين الأمرين لتحذيرك في الوقت المناسب.',
    'perm.loud': 'تنبيهات بصوت عالٍ',
    'perm.loudText': 'حتى يصلك تنبيه منطقتك فوراً، حتى لو كان الهاتف صامتاً.',
    'perm.location': 'الموقع، طوال الوقت',
    'perm.locationText': 'يحدد هاتفك المنطقة التي أنت فيها. لا يغادر موقعك هذا الهاتف أبداً.',
    'perm.allow': 'السماح',
    'home.noAlerts': 'لا توجد تنبيهات في {site}',
    'home.yourZone': 'منطقتك',
    'home.zoneUnknown': 'الموقع مغلق أو غير معروف. ستكون كل التنبيهات بصوت عالٍ.',
    'home.notInZone': 'لست في أي منطقة',
    'home.turnOn': 'تشغيل',
    'home.language': 'اللغة',
    'home.languageText': 'تستخدم التنبيهات وشاشة القفل هذه اللغة بدءاً من التنبيه التالي. هذه هي اللغات التي يوفرها {site}.',
    'home.change': 'تغيير',
    'home.recent': 'آخر التنبيهات',
    'home.lastHeard': 'آخر اتصال بـ Z.A.S.',
    'alert.notConfirmed': 'لم يتم التأكد من أنه صادر عن موقعك',
    'alert.close': 'إغلاق',
    'alert.turnOnLocation': 'شغّل الموقع',
    'alert.locationOff': 'الموقع مغلق، لذا لا نعرف أين أنت. إذا كنت في {zones}: {message}',
    'alert.locationUnknown': 'موقعك غير معروف حالياً. إذا كنت في {zones}: {message}',
    'alert.inside': 'أنت داخل المنطقة {zone}. {message}',
    'alert.notIn': 'أنت لست في {zones}. لا تتوجه إلى هناك.',
    'alert.near': 'كن متيقظاً إذا كنت قرب {zones}.',
    'off.title': 'نظام Z.A.S. متوقف في هذا الموقع',
    'off.text': 'لن تُرسل التنبيهات إلى هذا الهاتف. لا تعتمد عليه في التحذيرات. اسأل مشرفك.',
    'off.zone': 'لا يتم التتبع أثناء توقف Z.A.S.',
    'ops.centre': '{site} · مركز العمليات',
    'ops.offline': 'تعذّر الاتصال بـ Z.A.S.',
    'ops.alertIn.one': 'تنبيه في منطقة واحدة',
    'ops.alertIn.many': 'تنبيه في {count} مناطق',
    'ops.legendDanger': 'احتمِ أو أخلِ',
    'ops.zoneStatus': 'حالة المناطق',
    'ops.tapHint': 'انقر على المناطق في الخريطة لإرسال تنبيه',
    'ops.zoneClear': 'آمنة',
    'ops.sent': 'تم الإرسال · {title}',
    'ops.sending': 'جارٍ الإرسال إلى الهواتف…',
    'ops.noPhones': 'لم ينضم أي هاتف إلى هذا الموقع بعد',
    'ops.delivered.one': 'تم التسليم إلى {accepted} من هاتف واحد',
    'ops.delivered.many': 'تم التسليم إلى {accepted} من {attempted} هواتف',
    'ops.retrying': '{failed} قيد إعادة المحاولة',
    'ops.sendTo.one': 'إرسال إلى منطقة واحدة',
    'ops.sendTo.many': 'إرسال إلى {count} مناطق',
    'ops.cancel': 'إلغاء',
    'ops.level': 'المستوى',
    'ops.holdHint': 'اضغط مطولاً لمدة ثانية واحدة. يُرسل فوراً.',
    'ops.hold': 'اضغط مطولاً للإرسال',
    'ops.holdSending': 'جارٍ الإرسال…',
    'ops.notSent': 'لم يُرسل. حاول مرة أخرى، واتصل بفريق Z.A.S. إذا تكرر ذلك.',
    'ops.notSentOffline': 'لم يُرسل: تعذّر الاتصال بـ Z.A.S. تحقق من الاتصال وحاول مرة أخرى.',
    'ops.notSentOff': 'لم يُرسل: نظام Z.A.S. متوقف لهذا الموقع. اتصل بفريق Z.A.S.',
    'ops.updateFailed': 'لم يجتز تحديث الموقع فحوص الأمان. سيبقى الإعداد الحالي.',
    'ops.languageText': 'يعرض الجهاز اللوحي كل شيء بهذه اللغة. هذه هي اللغات التي يوفرها {site}.',
    'ops.testText': 'يتلقى كل هاتف انضم إلى {site} تنبيهاً تجريبياً. لا تتغير أي منطقة. يرى الموظفون:',
    'ops.off': 'نظام Z.A.S. متوقف لهذا الموقع. لا يمكن إرسال التنبيهات. اتصل بفريق Z.A.S.',
    'ops.approve.setUpTitle': 'اعتماد إعداد الموقع',
    'ops.approve.changeTitle': 'اعتماد تغيير على {site}',
    'ops.approve.text': 'راجع الخريطة والقائمة. بعد الاعتماد يصل الإعداد إلى هذا الجهاز اللوحي وإلى هواتف جميع الموظفين.',
    'ops.approve.prepared': 'أعدّه {name} · {time}',
    'ops.approve.hold': 'اضغط مطولاً للاعتماد',
    'ops.approve.holdHint': 'اضغط مطولاً لمدة ثانية واحدة للاعتماد.',
    'ops.approve.approving': 'جارٍ الاعتماد…',
    'ops.approve.failed': 'لم يتم الاعتماد. حاول مرة أخرى.',
    'ops.approve.waiting': 'يوجد تغيير بانتظار اعتمادك',
    'ops.approve.review': 'مراجعة',
    'ops.approve.later': 'لاحقاً',
    'change.siteSetUp.one': 'إعداد الموقع: منطقة واحدة، {languages}',
    'change.siteSetUp.many': 'إعداد الموقع: {count} مناطق، {languages}',
    'change.renameSite': 'تغيير اسم الموقع إلى {name}',
    'change.addZone': 'إضافة المنطقة {zone} · {name}',
    'change.renameZone': 'تغيير اسم المنطقة {zone} إلى {name}',
    'change.redrawZone': 'إعادة رسم المنطقة {zone} · {name}',
    'change.removeZone': 'حذف المنطقة {zone} · {name}',
    'change.mapArea': 'تغيير منطقة الخريطة',
    'change.mapPicture': 'تغيير صورة الخريطة',
    'change.addTablet': 'إضافة جهاز عمليات: {name}',
    'change.removeTablet': 'حذف جهاز عمليات: {name}',
    'change.addLanguage': 'إضافة لغة: {language}',
    'change.changeWording': 'تغيير الصياغة: {language}',
    'change.removeLanguage': 'حذف لغة: {language}',
    'change.more': 'و{count} تغييرات أخرى',
    'change.noChange': 'لا يوجد تغيير ظاهر (أُعيد التوقيع)',
    'change.separator': '؛ ',
  },
};

export const BLANK_NAMES = ['site', 'zone', 'zones', 'message', 'then', 'title', 'count', 'accepted', 'attempted', 'failed', 'name', 'time', 'language', 'languages', 'link', 'summary'] as const;
export type BlankName = (typeof BLANK_NAMES)[number];
const BLANK = new RegExp(`\\{(${BLANK_NAMES.join('|')})\\}`, 'g');
/** The blanks a phrase holds, sorted, e.g. ["message", "zones"]. */
export const blanksIn = (text: string): string[] => [...text.matchAll(BLANK)].map((m) => m[1]).sort();
const sameBlanks = (a: string, b: string) => blanksIn(a).join() === blanksIn(b).join();

/** Is this a complete, usable set of words? Every key, each filled in, keeping the English blanks. */
export function phrasesComplete(p: unknown): p is Phrases {
  if (typeof p !== 'object' || p === null) return false;
  const x = p as Record<string, unknown>;
  return PHRASE_KEYS.every((k) => typeof x[k] === 'string' && (x[k] as string).trim().length > 0 && sameBlanks(x[k] as string, STANDARD_PHRASES.en[k]));
}

/** What's still missing in a language, in plain words, for the console. */
export function phraseProblems(p: Partial<Record<string, string>> | undefined): { missing: PhraseKey[]; blanks: PhraseKey[] } {
  const missing: PhraseKey[] = [];
  const blanks: PhraseKey[] = [];
  for (const k of PHRASE_KEYS) {
    const v = p?.[k];
    if (typeof v !== 'string' || !v.trim()) missing.push(k);
    else if (!sameBlanks(v, STANDARD_PHRASES.en[k])) blanks.push(k);
  }
  return { missing, blanks };
}

/**
 * A site's words for a language. English and Arabic start from ours, with the site's own
 * changes on top (so words we add later are there too); any other language needs its
 * own complete set.
 */
export function phrasesFor(bundle: Pick<SiteBundle, 'phrases'>, language: string): Phrases | null {
  const own = bundle.phrases?.[language];
  const base = language === 'en' || language === 'ar' ? STANDARD_PHRASES[language] : undefined;
  const words = base ? { ...base, ...own } : own;
  if (phrasesComplete(words)) return words;
  return base ?? null;
}

/**
 * The languages a site offers its staff: the ones with all five alert instructions AND
 * every word of the app. English first, then Arabic, then the rest by code.
 */
export function completeLanguages(bundle: Pick<SiteBundle, 'messages' | 'phrases'>): string[] {
  const langs = Object.keys(bundle.messages).filter((l) => phrasesFor(bundle, l) !== null);
  const rank = (l: string) => (l === 'en' ? 0 : l === 'ar' ? 1 : 2);
  return langs.sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
}

/** The language a phone actually shows: its choice if the site offers it, otherwise English. */
export const shownLanguage = (bundle: Pick<SiteBundle, 'messages' | 'phrases'>, language: string): string =>
  completeLanguages(bundle).includes(language) ? language : 'en';

/** Fills a phrase's blanks. */
export function fill(text: string, values: Partial<Record<BlankName, string>>): string {
  return text.replace(BLANK, (_, name: BlankName) => values[name] ?? '');
}

/** The level's title in a set of words. */
export const levelTitle = (p: Phrases, level: Level): string => p[`title.${level}` as PhraseKey];
